import { useEffect, useState } from 'react'

import { Select, TextInput, Textarea } from '@mantine/core'

import { VoEProvider } from 'src/graphql/types/carriers'
import { VoECompletionMethod } from 'src/graphql/types/verificationOfEmployments'
import { cleanDirectoryPhone, sanitizeEmail } from 'src/lib/contact.utils'
import { sanitizeDotNumber } from 'src/lib/dot.utils'
import {
  VOE_PROVIDER_OPTIONS,
  voeProviderLabel,
} from 'src/lib/voeProvider.utils'

import ReadOnlyField from './ReadOnlyField'

export interface VoECompletionContact {
  contactName: string
  email: string
  phoneNumber: string
  faxNumber: string
}

export interface VoECompletionValue {
  completionMethod: VoECompletionMethod | null
  platform: VoEProvider | null
  contact: VoECompletionContact
  dotNumber: string
  note: string
}

/** What the verification already holds. */
export interface VoECompletionSubject {
  contactName?: string | null
  email?: string | null
  phoneNumber?: string | null
  faxNumber?: string | null
  dotNumber?: string | null
}

export const EMPTY_VOE_COMPLETION_CONTACT: VoECompletionContact = {
  contactName: '',
  email: '',
  phoneNumber: '',
  faxNumber: '',
}

export const EMPTY_VOE_COMPLETION: VoECompletionValue = {
  completionMethod: null,
  platform: null,
  contact: EMPTY_VOE_COMPLETION_CONTACT,
  dotNumber: '',
  note: '',
}

const METHOD_OPTIONS: Array<{ value: VoECompletionMethod; label: string }> = [
  { value: VoECompletionMethod.PHONE, label: 'Phone' },
  { value: VoECompletionMethod.FAX, label: 'Fax' },
  { value: VoECompletionMethod.EMAIL_EXTERNAL, label: 'Email' },
  // Short so all five chips fit one row of the small modal.
  { value: VoECompletionMethod.ATS_PLATFORM, label: 'ATS' },
  { value: VoECompletionMethod.NO_RESPONSE, label: 'No Response' },
]

type ContactChannelField = 'email' | 'phoneNumber' | 'faxNumber'

const CHANNELS: Partial<
  Record<VoECompletionMethod, { field: ContactChannelField; label: string }>
> = {
  [VoECompletionMethod.PHONE]: { field: 'phoneNumber', label: 'Phone' },
  [VoECompletionMethod.FAX]: { field: 'faxNumber', label: 'Fax' },
  [VoECompletionMethod.EMAIL_EXTERNAL]: { field: 'email', label: 'Email' },
}

const channelFor = (method: VoECompletionMethod | null) =>
  method ? CHANNELS[method] ?? null : null

const PROVIDER_VALUES = new Set<string>(Object.values(VoEProvider))

export const VOE_COMPLETION_NOTE_MAX_LENGTH = 2000

/** The VoE's own platform when known, else the carrier's preferred one. */
export const defaultVoECompletionPlatform = (
  voe:
    | {
        platform?: string | null
        carrierRouting?: { preferredPlatform?: string | null } | null
      }
    | null
    | undefined
): VoEProvider | null => {
  if (voe?.platform && PROVIDER_VALUES.has(voe.platform)) {
    return voe.platform as VoEProvider
  }
  const preferred = voe?.carrierRouting?.preferredPlatform
  return preferred && PROVIDER_VALUES.has(preferred)
    ? (preferred as VoEProvider)
    : null
}

const isDirectoryValue = (field: ContactChannelField, value: string) =>
  field === 'email' ? !!sanitizeEmail(value) : !!cleanDirectoryPhone(value)

/** What the verification holds for the method, as stored (for display). */
const storedChannelValue = (
  subject: VoECompletionSubject | null | undefined,
  method: VoECompletionMethod | null
) => {
  const channel = channelFor(method)
  return channel ? subject?.[channel.field]?.trim() ?? '' : ''
}

/**
 * The contact Save confirms. A stored value the directory can't use (an
 * international number, a malformed email) is shown but not sent, so it never
 * blocks the completion.
 */
const heldContactFor = (
  subject: VoECompletionSubject | null | undefined,
  method: VoECompletionMethod | null
): VoECompletionContact => {
  const channel = channelFor(method)
  if (!channel) return EMPTY_VOE_COMPLETION_CONTACT
  const stored = storedChannelValue(subject, method)
  return {
    ...EMPTY_VOE_COMPLETION_CONTACT,
    contactName: subject?.contactName?.trim() ?? '',
    [channel.field]: isDirectoryValue(channel.field, stored) ? stored : '',
  }
}

type FieldErrors = Partial<
  Record<ContactChannelField | 'dotNumber' | 'note', string>
>

const fieldErrors = (value: VoECompletionValue): FieldErrors => {
  const errors: FieldErrors = {}
  const { email, phoneNumber, faxNumber } = value.contact
  if (email.trim() && !sanitizeEmail(email)) {
    errors.email = 'Enter a valid email.'
  }
  if (phoneNumber.trim() && !cleanDirectoryPhone(phoneNumber)) {
    errors.phoneNumber = 'Enter a 10-digit phone number.'
  }
  if (faxNumber.trim() && !cleanDirectoryPhone(faxNumber)) {
    errors.faxNumber = 'Enter a 10-digit fax number.'
  }
  if (value.dotNumber.trim() && !sanitizeDotNumber(value.dotNumber)) {
    errors.dotNumber = 'Enter a valid DOT number.'
  }
  if (value.note.trim().length > VOE_COMPLETION_NOTE_MAX_LENGTH) {
    errors.note = 'Keep the note under 2,000 characters.'
  }
  return errors
}

export const isVoECompletionValid = (value: VoECompletionValue): boolean =>
  !!value.completionMethod &&
  (value.completionMethod !== VoECompletionMethod.ATS_PLATFORM ||
    !!value.platform) &&
  Object.keys(fieldErrors(value)).length === 0

export const toVoECompletionInput = (value: VoECompletionValue) => {
  const method = value.completionMethod
  const contactEntries = Object.entries(value.contact)
    .map(([key, text]) => [key, text.trim()] as const)
    .filter(([, text]) => text)
  const keepsContact = !!channelFor(method) && contactEntries.length > 0
  const dotNumber = value.dotNumber.trim()
  const note = value.note.trim()
  return {
    completionMethod: method,
    platform:
      method === VoECompletionMethod.ATS_PLATFORM ? value.platform : null,
    contact: keepsContact
      ? (Object.fromEntries(contactEntries) as Partial<VoECompletionContact>)
      : null,
    dotNumber:
      dotNumber && method !== VoECompletionMethod.NO_RESPONSE
        ? dotNumber
        : null,
    note: note || null,
  }
}

interface VoECompletionFieldsProps {
  value: VoECompletionValue
  onChange: (value: VoECompletionValue) => void
  defaultPlatform?: VoEProvider | null
  allowNoResponse?: boolean
  /** Omitted for a bulk change, which then never asks for a contact. */
  subject?: VoECompletionSubject | null
  showErrors?: boolean
  disabled?: boolean
}

/**
 * Shared by the completion modal and the VoE response upload. A contact the
 * verification already holds for the chosen method is shown read-only and
 * confirmed on save; inputs appear only when nothing is on file.
 */
const VoECompletionFields = ({
  value,
  onChange,
  defaultPlatform = null,
  allowNoResponse = true,
  subject = null,
  showErrors = false,
  disabled = false,
}: VoECompletionFieldsProps) => {
  const [editingContact, setEditingContact] = useState(false)
  const [editingPlatform, setEditingPlatform] = useState(false)
  const [touched, setTouched] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!value.completionMethod) {
      setEditingContact(false)
      setEditingPlatform(false)
      setTouched(new Set())
    }
  }, [value.completionMethod])

  const methods = allowNoResponse
    ? METHOD_OPTIONS
    : METHOD_OPTIONS.filter((m) => m.value !== VoECompletionMethod.NO_RESPONSE)
  const isAts = value.completionMethod === VoECompletionMethod.ATS_PLATFORM
  const channel = channelFor(value.completionMethod)
  const capturesContact = !!subject && !!channel
  const held = heldContactFor(subject, value.completionMethod)
  const storedValue = storedChannelValue(subject, value.completionMethod)
  const hasHeldContact = !!channel && !!storedValue
  const showContactInputs =
    capturesContact && (!hasHeldContact || editingContact)
  const asksForDot =
    !!subject &&
    !subject.dotNumber?.trim() &&
    !!value.completionMethod &&
    value.completionMethod !== VoECompletionMethod.NO_RESPONSE
  const platformReadOnly =
    isAts &&
    !editingPlatform &&
    !!defaultPlatform &&
    value.platform === defaultPlatform

  const errors = fieldErrors(value)
  const errorFor = (field: keyof FieldErrors) =>
    showErrors || touched.has(field) ? errors[field] : undefined
  const markTouched = (field: string) =>
    setTouched((previous) => new Set(previous).add(field))

  const selectMethod = (completionMethod: VoECompletionMethod) => {
    setEditingContact(false)
    onChange({
      ...value,
      completionMethod,
      platform:
        completionMethod === VoECompletionMethod.ATS_PLATFORM
          ? value.platform ?? defaultPlatform
          : value.platform,
      contact: subject
        ? heldContactFor(subject, completionMethod)
        : EMPTY_VOE_COMPLETION_CONTACT,
    })
  }

  const setContactField = (field: keyof VoECompletionContact, text: string) =>
    onChange({ ...value, contact: { ...value.contact, [field]: text } })

  const contactSummary = channel
    ? [held.contactName, storedValue].filter(Boolean).join(' · ')
    : ''
  const dotHint =
    asksForDot &&
    !value.dotNumber.trim() &&
    Object.values(value.contact).some((text) => text.trim())
      ? 'Add the DOT number to save the contact to the carrier directory too.'
      : null

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="mb-2 text-sm font-medium text-doubleNickel-gray-700">
          Completion Method{' '}
          <span className="text-doubleNickel-error-700">*</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {methods.map((method) => (
            <button
              key={method.value}
              type="button"
              disabled={disabled}
              aria-pressed={value.completionMethod === method.value}
              onClick={() => selectMethod(method.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                value.completionMethod === method.value
                  ? 'border-doubleNickel-brand-500 bg-doubleNickel-brand-500 text-white'
                  : 'border-doubleNickel-gray-300 bg-white text-doubleNickel-gray-700 hover:border-doubleNickel-brand-500 hover:text-doubleNickel-brand-500'
              }`}
            >
              {method.label}
            </button>
          ))}
        </div>
        {showErrors && !value.completionMethod && (
          <div className="mt-1 text-xs text-doubleNickel-error-700">
            Choose how the answer arrived to complete the verification.
          </div>
        )}
      </div>

      {isAts &&
        (platformReadOnly ? (
          <ReadOnlyField
            label="Platform"
            value={voeProviderLabel(value.platform)}
            onChange={() => setEditingPlatform(true)}
            disabled={disabled}
          />
        ) : (
          <Select
            label="Platform"
            withAsterisk
            placeholder="Select platform"
            data={VOE_PROVIDER_OPTIONS}
            value={value.platform}
            disabled={disabled}
            comboboxProps={{ withinPortal: true, zIndex: 1000 }}
            onChange={(platform) =>
              onChange({ ...value, platform: platform as VoEProvider | null })
            }
            error={
              showErrors && !value.platform
                ? 'Choose the platform the answer came through.'
                : undefined
            }
          />
        ))}

      {capturesContact && !showContactInputs && (
        <ReadOnlyField
          label="Contact"
          value={contactSummary}
          onChange={() => setEditingContact(true)}
          disabled={disabled}
        />
      )}

      {showContactInputs && channel && (
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium text-doubleNickel-gray-700">
            Contact{' '}
            <span className="text-xs font-normal text-doubleNickel-gray-500">
              optional
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              aria-label="Contact name"
              placeholder="Name"
              value={value.contact.contactName}
              disabled={disabled}
              onChange={(event) =>
                setContactField('contactName', event.currentTarget.value)
              }
            />
            <TextInput
              aria-label={`Contact ${channel.label.toLowerCase()}`}
              placeholder={channel.label}
              value={value.contact[channel.field]}
              disabled={disabled}
              onChange={(event) =>
                setContactField(channel.field, event.currentTarget.value)
              }
              onBlur={() => markTouched(channel.field)}
              error={errorFor(channel.field)}
            />
          </div>
        </div>
      )}

      {asksForDot && (
        <div className="flex flex-col gap-1">
          <TextInput
            label={
              <span>
                DOT Number{' '}
                <span className="text-xs font-normal text-doubleNickel-gray-500">
                  optional
                </span>
              </span>
            }
            placeholder="DOT Number"
            inputMode="numeric"
            value={value.dotNumber}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...value, dotNumber: event.currentTarget.value })
            }
            onBlur={() => markTouched('dotNumber')}
            error={errorFor('dotNumber')}
          />
          {dotHint && (
            <div className="text-xs text-doubleNickel-gray-500">{dotHint}</div>
          )}
        </div>
      )}

      {value.completionMethod && (
        <Textarea
          label={
            <span>
              Note{' '}
              <span className="text-xs font-normal text-doubleNickel-gray-500">
                optional
              </span>
            </span>
          }
          autosize
          minRows={2}
          maxRows={6}
          value={value.note}
          disabled={disabled}
          onChange={(event) =>
            onChange({ ...value, note: event.currentTarget.value })
          }
          error={errors.note}
        />
      )}
    </div>
  )
}

export default VoECompletionFields
